import React from 'react';

import { colors } from '../../theme';
import type { LiveMarketData } from '../../types';

interface MarketDataFooterProps {
  liveData: LiveMarketData;
  sharesOutstanding: number;
  netDebt: number;
}

export function MarketDataFooter({
  liveData,
  sharesOutstanding,
  netDebt,
}: MarketDataFooterProps): React.ReactElement {
  return (
    <div className='shrink-0 border-t border-edge bg-white text-xs'>
      {/* Section header */}
      <div className='flex items-center justify-between border-b border-edge bg-white px-2 pt-2 pb-1 text-[10px] font-bold uppercase tracking-wide text-navy'>
        <div className='flex items-center gap-1.5'>
          <div
            className='h-1.5 w-1.5 shrink-0 rounded-full'
            style={{
              background: liveData.isLoading
                ? colors.inkMuted
                : liveData.error
                  ? colors.negative
                  : colors.positive,
            }}
          />
          Market Data (Last Close)
        </div>
        {!liveData.isLoading && !liveData.error && (
          <span className='font-normal tracking-normal normal-case'>
            {liveData.latestTradingDay}
          </span>
        )}
      </div>

      {liveData.isLoading ? (
        <div className='px-2 py-3 text-center text-[11px] text-ink-muted'>
          <div className='mx-auto mb-2 h-4 w-4 animate-spin rounded-full border-2 border-edge border-t-navy' />
          Fetching live data...
        </div>
      ) : liveData.error ? (
        <div className='px-2 py-3 text-center text-[11px] text-ink-muted'>
          {liveData.error}
        </div>
      ) : (
        <>
          {[
            {
              label: 'Current price',
              value: `$${liveData.price.toFixed(2)}`,
              isChange: false,
            },
            {
              label: 'Day change',
              value: `${liveData.changePercent >= 0 ? '+' : '−'}${Math.abs(liveData.changePercent).toFixed(2)}%`,
              isChange: true,
            },
            {
              label: 'Market cap',
              value: `$${((liveData.price * sharesOutstanding) / 1000000).toFixed(2)}B`,
              isChange: false,
            },
            {
              label: 'Enterprise value',
              value: `$${((liveData.price * sharesOutstanding + netDebt) / 1000000).toFixed(2)}B`,
              isChange: false,
            },
          ].map((row, idx) => (
            <div
              key={row.label}
              className='grid grid-cols-[1fr_auto] gap-x-4 px-2 py-1'
              style={{
                background: idx % 2 === 0 ? '#fff' : colors.surfaceAlt,
              }}
            >
              <div className='text-[11px] font-normal text-ink-secondary'>
                {row.label}
              </div>
              <div
                className={`font-mono text-[11px] font-medium ${
                  !row.isChange ? 'text-ink' : ''
                }`}
                style={
                  row.isChange
                    ? {
                        color:
                          liveData.changePercent >= 0
                            ? colors.positive
                            : colors.negative,
                      }
                    : undefined
                }
              >
                {row.value}
              </div>
            </div>
          ))}
          <div className='px-2 py-1 text-right text-[9px] text-ink-muted'>
            Source: Alpha Vantage
          </div>
        </>
      )}
    </div>
  );
}
